import { liveQuery } from "dexie";
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import { v5 as uuidv5 } from "uuid";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { getCurrentAssistantId } from "@/store/assistant/assistant-id";
import { CreateSessionEventOperation } from "@/store/sync/operations/sessions/CreateSessionEventOperation";
import { fetchSessionComboboxData } from "@/store/sessions/search/session-combobox-fetch";
import { MDSComboboxFetchDataHandler, MDSComboboxItem } from "@/mds/components/forms";
import { MDSComboboxFetchDataHandlerSessionInputFormContext } from "@/mds/components/sessions/session.types";
import { SessionEventModelData, SessionKind } from "@/store/sessions/types";
import { SessionObservable } from "@/store/sessions/SessionObservable";
import { Maybe } from "@/domains/common/types";
import { logger } from "@/modules/logger";
import { uuidModule } from "@/modules/uuid";
import { GatedFeature } from "@/store/feature-gates/types";
import { PaywallContext, trackMemAgentMessageSent } from "@/domains/metrics";
import { clientEnvModule } from "@/modules/client-env";
import { electronModule } from "@/modules/electron";
import type { FloatingAgentFirstMessageOnboardingCompletionTrigger } from "@/modules/electron/types";
import { trackFloatingAgentUpgradeButtonClicked } from "@/modules/mem-agent/upgrade-button-tracking";

/** The widget's one immortal session kind. */
export const FLOATING_AGENT_SESSION_KIND: SessionKind = "NATIVE_CHAT";

/** Stable namespace for deterministic agent-chat session IDs. */
const AGENT_CHAT_SESSION_NAMESPACE = "5b6eb8e9-7874-4ebf-87d1-d106785fa63a";

/**
 * Each (space account, assistant) pair has one UUIDv5-derived agent-chat
 * session ID. Events on any other NATIVE_CHAT session fail ownership checks.
 */
export const resolveDefaultAgentChatSessionId = ({
  spaceAccountId,
  assistantId,
}: {
  spaceAccountId: string;
  assistantId: string;
}): string => {
  return uuidv5(
    `${spaceAccountId.toUpperCase()}::${assistantId.toUpperCase()}`,
    AGENT_CHAT_SESSION_NAMESPACE
  );
};

/**
 * Raw events, not visible rows: one agent turn emits several, and thread
 * replies spend the budget without appearing in the main list. The window is
 * relationship-blind, so a thread root falling outside it detaches its replies
 * — matching iOS's 500-event thread floor makes that cutoff rare until the
 * window becomes ancestry-aware.
 */
export const EVENT_WINDOW_PAGE_SIZE = 500;

/**
 * The server files every NATIVE_CHAT event into one default SessionThread
 * and re-roots reply chains to the exchange's first message, so neither
 * session_thread_id nor root_session_event_id distinguishes a thread reply
 * from the operator's inline answer (both arrive with the same refs). What
 * does: users set reply_to only when replying in a thread, and the operator
 * always sets reply_to to the user message that woke it. A user event with
 * reply_to is therefore a thread reply anchored at its target, and an agent
 * event is a thread member when it answers a thread reply — or when it
 * answers an agent-authored thread root (the re-rooted later messages of an
 * operator turn inside that thread; see the second pass below).
 */
const isUserAuthoredEvent = (event: SessionEventModelData): boolean =>
  event.kind === "USER_ACTION_V1";

/**
 * Kinds that count toward unread state and notifications: operator replies and
 * system notices. AGENT_ACTION_V1 (thinking/tool steps) and AGENT_DIGEST_V1
 * never badge — a digest isn't renderable, so it would otherwise produce an
 * unread state the user can't clear by looking. The MARK_SESSION_SEEN
 * watermark scan stays broader than this predicate; see
 * findLatestFloatingAgentSeenEventAt.
 */
export const isNotifiableSessionEvent = (event: SessionEventModelData): boolean =>
  event.kind === "AGENT_PLANNING_V1" || event.kind === "SYSTEM_NOTIFICATION_V1";

type VisibleThreadReplyAuthor = "agent" | "user";

const VISIBLE_THREAD_REPLY_PART_KINDS = new Set([
  "MARKDOWN_V1",
  "WEB_CLIP_V1",
  "ARTIFACT_V1",
  "FACT_CARD_V1",
]);

const resolveVisibleThreadReplyAuthor = (
  event: SessionEventModelData
): VisibleThreadReplyAuthor | null => {
  let author: VisibleThreadReplyAuthor;
  if (isUserAuthoredEvent(event)) {
    author = "user";
  } else if (
    event.kind === "AGENT_ACTION_V1" ||
    event.kind === "AGENT_PLANNING_V1" ||
    event.kind === "SYSTEM_NOTIFICATION_V1"
  ) {
    author = "agent";
  } else {
    return null;
  }

  const value = event.value_json;
  if (!value || typeof value !== "object") return null;
  const parts = "parts" in value ? value.parts : undefined;
  if (!Array.isArray(parts)) return null;

  const hasVisibleMessagePart = parts.some(
    part =>
      !!part &&
      typeof part === "object" &&
      "kind" in part &&
      typeof part.kind === "string" &&
      VISIBLE_THREAD_REPLY_PART_KINDS.has(part.kind)
  );
  return hasVisibleMessagePart ? author : null;
};

const isIosShareExtensionWebClipRoot = (event: SessionEventModelData): boolean => {
  if (event.root_session_event_id !== event.id) return false;
  const value = event.value_json;
  if (!value || typeof value !== "object") return false;
  const context = "context" in value ? value.context : undefined;
  const parts = "parts" in value ? value.parts : undefined;
  return (
    !!context &&
    typeof context === "object" &&
    "surface" in context &&
    context.surface === "ios_share_extension" &&
    "input_modality" in context &&
    context.input_modality === "web_clip" &&
    Array.isArray(parts) &&
    parts.some(
      part =>
        !!part &&
        typeof part === "object" &&
        "kind" in part &&
        part.kind === "WEB_CLIP_V1" &&
        (!("is_primary_clip" in part) || part.is_primary_clip !== false)
    )
  );
};

export interface ThreadReplySummary {
  replyCount: number;
  hasUserReply: boolean;
  hasAgentReply: boolean;
}

/**
 * Data layer for the floating-agent widget: finds or creates the user's
 * single NATIVE_CHAT session, exposes a windowed live view of its events
 * (an immortal thread must not hydrate unbounded history), and submits user
 * messages through the standard sync outbox.
 */
export class FloatingAgentPageStore extends AppSubStore {
  sessionId: string | null = null;

  private eventWindowLimit = EVENT_WINDOW_PAGE_SIZE;
  private subscribedSessionEvents: SessionEventModelData[] = [];
  private liveQuerySubscription: { unsubscribe: () => void } | null = null;
  private disposeWindowReaction: (() => void) | null = null;
  private ensureSessionPromise: Promise<string> | null = null;
  private upsertNativeChatSessionPromise: Promise<string> | null = null;
  private assistantIdProvisionPromise: Promise<string | undefined> | null = null;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    makeObservable<
      FloatingAgentPageStore,
      | "eventWindowLimit"
      | "subscribedSessionEvents"
      | "liveQuerySubscription"
      | "disposeWindowReaction"
      | "initializeEventsLiveQuery"
      | "ensureSession"
      | "adoptOrCreateSession"
      | "upsertNativeChatSession"
      | "ensureSessionPromise"
      | "upsertNativeChatSessionPromise"
      | "assistantIdProvisionPromise"
      | "deriveSessionId"
      | "findExistingSessionId"
      | "openNativeChatLimitUpsell"
      | "completeFirstMessageOnboardingIfNeeded"
      | "findFirstUnread"
      | "countFromAnchor"
      | "isUnread"
    >(this, {
      sessionId: observable,
      eventWindowLimit: observable,
      subscribedSessionEvents: observable.ref,
      liveQuerySubscription: false,
      disposeWindowReaction: false,
      session: computed,
      sessionEvents: computed,
      threadAnchorByEventId: computed,
      rootEvents: computed,
      nativeChatMessageFeatureGate: computed,
      isNativeChatMessageQuotaReady: computed,
      isNativeChatMessageInputDisabled: computed,
      threadSummaryByRootEventId: computed,
      firstUnreadRootEvent: computed,
      firstUnreadThreadReply: false,
      unreadCountFromAnchor: false,
      unreadThreadCountFromAnchor: false,
      findFirstUnread: false,
      countFromAnchor: false,
      isUnread: false,
      threadReplies: false,
      threadIdForRoot: false,
      initialize: action,
      loadOlderEvents: action,
      handleComboboxFetch: false,
      sendUserMessage: false,
      completeFirstMessageOnboardingIfNeeded: false,
      openNativeChatLimitUpsellForCurrentGate: false,
      openProactiveUpsell: false,
      retryExchange: false,
      ensureSessionForOnboarding: false,
      initializeEventsLiveQuery: false,
      ensureSession: false,
      adoptOrCreateSession: false,
      upsertNativeChatSession: false,
      ensureSessionPromise: false,
      upsertNativeChatSessionPromise: false,
      assistantIdProvisionPromise: false,
      deriveSessionId: false,
      findExistingSessionId: false,
      openNativeChatLimitUpsell: false,
      dispose: false,
    });
  }

  get session(): Maybe<SessionObservable> {
    if (!this.sessionId) return undefined;
    return this.store.sessions.get(this.sessionId);
  }

  /** Newest `eventWindowLimit` events, refreshed by Dexie liveQuery. */
  get sessionEvents(): SessionEventModelData[] {
    return this.subscribedSessionEvents;
  }

  get nativeChatMessageFeatureGate(): {
    allowed: boolean;
    used: number;
    limit: number;
    warnAt: number | null;
  } {
    return this.store.featureGates.checkGate(GatedFeature.NativeChatMessageSend);
  }

  /** The composer stays inert until the local native-chat usage counter has emitted. */
  get isNativeChatMessageQuotaReady(): boolean {
    return this.store.featureGates.nativeChatMessageCountLoaded;
  }

  get isNativeChatMessageInputDisabled(): boolean {
    const nativeChatMessageFeatureGate = this.nativeChatMessageFeatureGate;
    return !this.isNativeChatMessageQuotaReady || !nativeChatMessageFeatureGate.allowed;
  }

  /**
   * Thread anchor per event id; events absent from the map are main-list.
   * User replies anchor at their reply target; agent events inherit the
   * anchor of the message they answer (an answer to a main-list message —
   * or to a target outside the loaded window — stays inline).
   */
  get threadAnchorByEventId(): ReadonlyMap<string, string> {
    const eventsById = new Map(this.subscribedSessionEvents.map(event => [event.id, event]));
    const anchors = new Map<string, string>();
    const shareRootIds = new Set(
      this.subscribedSessionEvents.filter(isIosShareExtensionWebClipRoot).map(event => event.id)
    );

    // Share-extension captures are server-created user-started threads whose
    // root event intentionally roots to itself. Keep that capture in the main
    // list and file every descendant into its thread. Restricting this rule to
    // explicit share provenance avoids moving ordinary inline default-thread
    // answers when their user root later gains a thread.
    for (const event of this.subscribedSessionEvents) {
      const rootId = event.root_session_event_id;
      if (event.id !== rootId && rootId && shareRootIds.has(rootId)) {
        anchors.set(event.id, rootId);
      }
    }
    const anchorOf = (event: SessionEventModelData, seen: Set<string>): string | null => {
      const known = anchors.get(event.id);
      if (known) return known;
      const replyToId = event.reply_to_session_event_id;
      if (!replyToId || seen.has(event.id)) return null;
      seen.add(event.id);

      let anchor: string | null = null;
      if (isUserAuthoredEvent(event)) {
        anchor = replyToId;
      } else {
        const target = eventsById.get(replyToId);
        anchor = target ? anchorOf(target, seen) : null;
      }
      if (anchor) anchors.set(event.id, anchor);
      return anchor;
    };
    for (const event of this.subscribedSessionEvents) {
      anchorOf(event, new Set());
    }

    // Second pass: the server re-roots an operator turn's later messages to
    // the exchange's FIRST message. Inside a thread that first message is the
    // thread root itself, so those events arrive with reply_to = root and
    // resolve no anchor above. An unanchored agent event replying to an
    // AGENT-authored thread root belongs to that root's thread. (Agent-
    // authored roots only: inline main-list answers legitimately reply_to
    // *user* messages, which may also carry threads of their own.)
    const threadRootIds = new Set(anchors.values());
    for (const event of this.subscribedSessionEvents) {
      if (anchors.has(event.id) || isUserAuthoredEvent(event)) continue;

      const replyToId = event.reply_to_session_event_id;
      if (!replyToId || !threadRootIds.has(replyToId)) continue;

      const target = eventsById.get(replyToId);
      if (target && !isUserAuthoredEvent(target)) {
        anchors.set(event.id, replyToId);
      }
    }

    return anchors;
  }

  /** Main-list events: everything not living inside a thread. */
  get rootEvents(): SessionEventModelData[] {
    const anchors = this.threadAnchorByEventId;
    return this.subscribedSessionEvents.filter(event => !anchors.has(event.id));
  }

  /**
   * Oldest main-list event the owner has not seen, or null when the synced
   * watermark covers everything. Opening the widget lands here instead of the
   * bottom. Only notifiable events qualify: user-authored events are always
   * newer than the watermark and would scroll the user to their own message,
   * and thinking steps and digests never badge.
   */
  get firstUnreadRootEvent(): SessionEventModelData | null {
    return this.findFirstUnread(this.rootEvents);
  }

  /** The thread-pane equivalent, scoped to one thread's replies. */
  firstUnreadThreadReply(rootEventId: string): SessionEventModelData | null {
    return this.findFirstUnread(this.threadReplies(rootEventId));
  }

  /**
   * How many main-list events are at or after `anchorEventId` — what the
   * jump-to-latest pill reports.
   *
   * Counted from the caller's latched anchor rather than the live watermark:
   * opening the panel marks replies seen within a tick, so a watermark read
   * comes back 0 while the "New" divider is still on screen.
   *
   * Compared by timestamp, not array position: `rootEvents` is an unsorted
   * filter over the subscription, so slicing it by index counts whatever
   * happens to follow in insertion order.
   */
  unreadCountFromAnchor(anchorEventId: string | undefined): number {
    return this.countFromAnchor(this.rootEvents, anchorEventId);
  }

  /** The thread-pane equivalent, scoped to one thread's replies. */
  unreadThreadCountFromAnchor(rootEventId: string, anchorEventId: string | undefined): number {
    return this.countFromAnchor(this.threadReplies(rootEventId), anchorEventId);
  }

  private countFromAnchor(
    events: SessionEventModelData[],
    anchorEventId: string | undefined
  ): number {
    if (!anchorEventId) return 0;
    const anchor = events.find(event => event.id === anchorEventId);
    // The anchor may have aged out of the loaded window.
    if (!anchor) return 0;
    return events.filter(
      event =>
        isNotifiableSessionEvent(event) && event.locally_created_at >= anchor.locally_created_at
    ).length;
  }

  private isUnread(event: SessionEventModelData): boolean {
    const watermark = this.session?.ownerLastSeenEventAt ?? "";
    if (!isNotifiableSessionEvent(event)) return false;
    return event.locally_created_at > watermark;
  }

  private findFirstUnread(events: SessionEventModelData[]): SessionEventModelData | null {
    let firstUnread: SessionEventModelData | null = null;
    for (const event of events) {
      if (!this.isUnread(event)) continue;
      if (!firstUnread || event.locally_created_at < firstUnread.locally_created_at) {
        firstUnread = event;
      }
    }
    return firstUnread;
  }

  /**
   * Per-thread reply summary keyed by the anchor event id. The count reflects
   * visible conversation turns: adjacent messages from the same author are
   * one reply, and internal thinking events neither count nor split a turn.
   * Participant flags drive the avatar cluster on the "N replies" row.
   */
  get threadSummaryByRootEventId(): ReadonlyMap<string, ThreadReplySummary> {
    const summaries = new Map<string, ThreadReplySummary>();
    const lastVisibleAuthorByAnchor = new Map<string, VisibleThreadReplyAuthor>();
    const anchors = this.threadAnchorByEventId;
    const chronologicalEvents = this.subscribedSessionEvents
      .slice()
      .sort((a, b) => a.locally_created_at.localeCompare(b.locally_created_at));

    for (const event of chronologicalEvents) {
      const anchorId = anchors.get(event.id);
      if (!anchorId) continue;

      const author = resolveVisibleThreadReplyAuthor(event);
      if (!author) continue;

      const summary = summaries.get(anchorId) ?? {
        replyCount: 0,
        hasUserReply: false,
        hasAgentReply: false,
      };

      if (lastVisibleAuthorByAnchor.get(anchorId) !== author) {
        summary.replyCount += 1;
        lastVisibleAuthorByAnchor.set(anchorId, author);
      }

      if (author === "user") {
        summary.hasUserReply = true;
      } else {
        summary.hasAgentReply = true;
      }

      summaries.set(anchorId, summary);
    }
    return summaries;
  }

  /** A thread's replies, oldest first. */
  threadReplies(rootEventId: string): SessionEventModelData[] {
    const anchors = this.threadAnchorByEventId;
    return this.subscribedSessionEvents
      .filter(event => anchors.get(event.id) === rootEventId)
      .sort((a, b) => a.locally_created_at.localeCompare(b.locally_created_at));
  }

  /** The thread id once the server materialized it (undefined before). */
  threadIdForRoot(rootEventId: string): string | undefined {
    const anchors = this.threadAnchorByEventId;
    for (const event of this.subscribedSessionEvents) {
      if (anchors.get(event.id) === rootEventId && event.session_thread_id) {
        return event.session_thread_id;
      }
    }
    return undefined;
  }

  /**
   * Provisions the assistant, adopts its deterministic Native Chat session,
   * and creates that session through Core API when it is missing so the
   * connection-triggered introduction has a durable destination. Callers that
   * are handing onboarding to another channel may suppress creation while
   * still adopting an existing session.
   */
  async initialize({
    ensureOnboardingSession = true,
  }: { ensureOnboardingSession?: boolean } = {}): Promise<void> {
    this.initializeEventsLiveQuery();
    // The widget window has no recents UI observing the recents store, so
    // its lazy liveQuery never starts on its own; warm it for @-mentions.
    this.store.recentItems.initialize();
    if (this.sessionId) return;

    const existingId = await this.findExistingSessionId();
    if (existingId) {
      runInAction(() => {
        this.sessionId = this.sessionId ?? existingId;
      });
      return;
    }

    if (!ensureOnboardingSession) return;

    await this.ensureSessionForOnboarding();
  }

  /**
   * Ensures the onboarding session and its connection trigger can be recorded.
   * The generated greeting may still be in progress when this resolves.
   */
  async ensureSessionForOnboarding(): Promise<void> {
    const sessionId = await this.upsertNativeChatSession();
    runInAction(() => {
      this.sessionId = sessionId;
    });
  }

  /**
   * Grows the window by one page. The MDSSession event list is additive, so
   * a larger window simply upserts the older page into the rendered list.
   */
  loadOlderEvents(): void {
    // Fewer rows than the limit means the window already covers all history.
    if (this.subscribedSessionEvents.length < this.eventWindowLimit) return;
    this.eventWindowLimit += EVENT_WINDOW_PAGE_SIZE;
  }

  /**
   * Submits a USER_ACTION_V1 event through the sync outbox: optimistic Dexie
   * row (renders immediately via liveQuery), offline queueing, retry — all
   * existing machinery. Voice-originated messages carry
   * `source = "voice"` metadata on the markdown part, written as `_meta` and
   * read back as `client_meta`.
   *
   * `eventId` lets a caller converge a client-side placeholder bubble (e.g.
   * a pending voice clip) into the real event: same id → the optimistic row
   * overwrites the placeholder in MDSSession instead of duplicating it.
   *
   * `replyTo` makes the message a thread reply. The server owns thread
   * materialization: it lazily creates the SessionThread on the first reply
   * and derives root_session_event_id; the client only names the reply
   * target (plus the thread id once known, so validation is exact).
   */
  /** Note/collection @-mention search used by the chat composer. */
  handleComboboxFetch: MDSComboboxFetchDataHandler<MDSComboboxItem> = async ({
    query,
    mentionSymbol,
  }) => {
    return fetchSessionComboboxData({
      store: this.store,
      query,
      mentionSymbol,
    });
  };

  async sendUserMessage({
    content,
    source,
    eventId,
    replyTo,
    context,
  }: {
    content: string;
    source: "text" | "voice";
    eventId?: string;
    replyTo?: { eventId: string; sessionThreadId?: string };
    context?: MDSComboboxFetchDataHandlerSessionInputFormContext;
  }): Promise<boolean> {
    const trimmed = content.trim();
    if (trimmed.length === 0) return false;

    const nativeChatMessageFeatureGate = this.nativeChatMessageFeatureGate;
    if (!this.isNativeChatMessageQuotaReady) {
      // Silent by design elsewhere; logged here because a counter that never
      // loads makes this branch swallow sends until restart (MEM-19159).
      logger.warn({
        message: "[FloatingAgentPageStore] send blocked — quota counter not loaded",
      });
      return false;
    }
    if (!nativeChatMessageFeatureGate.allowed) {
      logger.info({
        message: "[FloatingAgentPageStore] send blocked by native-chat quota gate",
        info: {
          used: nativeChatMessageFeatureGate.used,
          limit: nativeChatMessageFeatureGate.limit,
        },
      });
      await this.openNativeChatLimitUpsell({
        used: nativeChatMessageFeatureGate.used,
        limit: nativeChatMessageFeatureGate.limit,
      });
      return false;
    }

    let sessionId: string;
    try {
      sessionId = await this.ensureSession();
    } catch (error) {
      logger.error({
        message: "[FloatingAgentPageStore] send failed: could not ensure the session",
        info: { error: String(error) },
      });
      return false;
    }

    const sessionEventId = eventId ?? uuidModule.generate();

    await new CreateSessionEventOperation({
      store: this.store,
      payload: {
        id: sessionEventId,
        session_id: sessionId,
        event_kind: "USER_ACTION_V1",
        locally_created_at: new Date().toISOString(),
        ...(replyTo
          ? {
              reply_to_session_event_id: replyTo.eventId,
              ...(replyTo.sessionThreadId ? { session_thread_id: replyTo.sessionThreadId } : {}),
            }
          : {}),
        value_json: {
          kind: "USER_ACTION_V1",
          parts: [
            {
              kind: "MARKDOWN_V1",
              id: uuidModule.generate(),
              content: trimmed,
              // `_meta` is the only spelling sync ingress accepts; it is
              // stored and returned as `client_meta`.
              ...(source === "voice" ? { _meta: { source: "voice" } } : {}),
            },
          ],
          context: context ?? null,
        },
      },
    }).execute();

    await this.store.sessions.updateSessionOnNewUserMessage(sessionId, {
      lastUserActionAt: new Date().toISOString(),
    });

    trackMemAgentMessageSent({
      inputMethod: source,
      sessionEventId,
    });
    this.completeFirstMessageOnboardingIfNeeded("message-sent");

    // No processing trigger: the operator work queue keys off event creation
    // for NATIVE_CHAT sessions.
    return true;
  }

  /**
   * Completes eligible first-message onboarding and notifies desktop when present.
   *
   * Legacy new-user onboarding accepts the earliest meaningful interaction:
   * opening the panel, confirming a voice hold, or successfully sending a
   * message. The intent quick-thought branch still requires a successful send
   * so these broader escape hatches cannot skip its explicit try-it step.
   *
   * Always report desktop interaction to the main window: the standalone
   * window can prewarm before onboarding activates, leaving its cache stale.
   */
  completeFirstMessageOnboardingIfNeeded(
    trigger: FloatingAgentFirstMessageOnboardingCompletionTrigger
  ): void {
    const perDeviceOnboardingInfo = this.store.platformInfo.perDeviceOnboardingInfo;
    let completedOnboarding = false;
    const intentOnboardingInfo = this.store.platformInfo.onboardingInfo;
    const isIntentQuickThoughtCapture =
      intentOnboardingInfo.onboardingFlowVersion === "intent_v1" &&
      intentOnboardingInfo.onboardingIntent === "quick_thoughts";
    const shouldCompleteNewUserOnboarding =
      !isIntentQuickThoughtCapture || trigger === "message-sent";

    if (
      shouldCompleteNewUserOnboarding &&
      perDeviceOnboardingInfo.memAgentOnboardingIsRelevant &&
      !perDeviceOnboardingInfo.hasCompletedMemAgentShortcutOnboarding
    ) {
      perDeviceOnboardingInfo.completeMemAgentShortcutOnboarding();
      completedOnboarding = true;
    }

    if (
      shouldCompleteNewUserOnboarding &&
      perDeviceOnboardingInfo.memAgentOnboardingIsRelevant &&
      !perDeviceOnboardingInfo.hasCompletedMemAgentFirstMessageOnboarding
    ) {
      perDeviceOnboardingInfo.completeMemAgentFirstMessageOnboarding();
      completedOnboarding = true;
    }

    if (
      perDeviceOnboardingInfo.existingUserMemAgentOnboardingIsRelevant &&
      !this.store.platformInfo.onboardingInfo.hasCompletedTask(
        "existing-user-mem-agent-first-message"
      )
    ) {
      void this.store.platformInfo.onboardingInfo.completeOnboardingTasks([
        "existing-user-mem-agent-first-message",
      ]);
      completedOnboarding = true;
    }

    if (clientEnvModule.isDesktop() || completedOnboarding) {
      void electronModule.floatingAgent.completeFirstMessageOnboarding({
        trigger,
      });
    }
  }

  async openNativeChatLimitUpsellForCurrentGate(): Promise<void> {
    const nativeChatMessageFeatureGate = this.nativeChatMessageFeatureGate;
    if (!this.isNativeChatMessageQuotaReady) return;

    await this.openNativeChatLimitUpsell({
      used: nativeChatMessageFeatureGate.used,
      limit: nativeChatMessageFeatureGate.limit,
    });
  }

  /**
   * Header Upgrade CTA. The standalone widget window renders no modal layer,
   * so desktop hands off to the main window, which focuses itself, opens the
   * modal, and tracks the click in the renderer that owns subscription state.
   */
  async openProactiveUpsell(): Promise<void> {
    if (clientEnvModule.isDesktop()) {
      try {
        await electronModule.floatingAgent.openProactiveUpsell();
      } catch (error) {
        logger.warn({
          message: "[FloatingAgentPageStore] failed to open Proactive upsell in main renderer",
          info: { error: String(error) },
        });
      }
      return;
    }

    trackFloatingAgentUpgradeButtonClicked({ store: this.store });
    this.store.subscriptions.openProactiveUpsell();
  }

  /**
   * Error-state retry. Re-drives the session via the process endpoint (Mem
   * It parity); the operator-side retry vehicle for NATIVE_CHAT is an open
   * item in the FE<>BE contract.
   */
  async retryExchange(): Promise<void> {
    if (!this.sessionId) return;
    await this.store.sessions.processSession(this.sessionId);
  }

  dispose(): void {
    this.disposeWindowReaction?.();
    this.disposeWindowReaction = null;
    this.liveQuerySubscription?.unsubscribe();
    this.liveQuerySubscription = null;
  }

  /**
   * Adopt-or-create, serialized: concurrent callers (a PTT release racing
   * boot-time initialize, double submits) share one in-flight promise, so
   * exactly one session can be created and `sessionId` — which the live
   * query reaction watches — is always assigned before a send proceeds.
   */
  private ensureSession(): Promise<string> {
    this.ensureSessionPromise ??= (async () => {
      try {
        const currentSessionId = this.sessionId;
        if (currentSessionId) {
          const currentRow = await this.store.sessions.localTable.get(currentSessionId);
          if (currentRow) return currentSessionId;
          runInAction(() => {
            if (this.sessionId === currentSessionId) this.sessionId = null;
          });
        }
        const sessionId = await this.adoptOrCreateSession();
        runInAction(() => {
          this.sessionId = sessionId;
        });
        return sessionId;
      } finally {
        // Every call rechecks the local row so a sync tombstone can invalidate
        // the deterministic session; failure clears the memo for the next send.
        this.ensureSessionPromise = null;
      }
    })();

    return this.ensureSessionPromise;
  }

  private async adoptOrCreateSession(): Promise<string> {
    const sessionId = await this.deriveSessionId();
    if (!sessionId) {
      throw new Error(
        "[FloatingAgentPageStore] Cannot derive the agent chat session id (no assistant)."
      );
    }

    const existingRow = await this.store.sessions.localTable.get(sessionId);
    if (existingRow) return sessionId;

    // Devices racing to create converge on the same derived id.
    await this.store.sessions.createSession({
      sessionId,
      status: "INITIAL",
      kind: FLOATING_AGENT_SESSION_KIND,
    });
    return sessionId;
  }

  private upsertNativeChatSession(): Promise<string> {
    this.upsertNativeChatSessionPromise ??= (async () => {
      try {
        const sessionId = await this.deriveSessionId();
        if (!sessionId) {
          throw new Error(
            "[FloatingAgentPageStore] Cannot derive the agent chat session id (no assistant)."
          );
        }

        await this.store.sessions.upsertSession({
          sessionId,
          kind: FLOATING_AGENT_SESSION_KIND,
        });
        return sessionId;
      } finally {
        this.upsertNativeChatSessionPromise = null;
      }
    })();

    return this.upsertNativeChatSessionPromise;
  }

  private async openNativeChatLimitUpsell({ used, limit }: { used: number; limit: number }) {
    if (clientEnvModule.isDesktop()) {
      try {
        await electronModule.floatingAgent.openNativeChatLimitUpsell({
          used,
          limit,
        });
      } catch (error) {
        logger.warn({
          message:
            "[FloatingAgentPageStore] failed to open native chat limit upsell in main renderer",
          info: { error: String(error) },
        });
      }
      return;
    }

    this.store.featureGates.openUsageLimitUpsell({
      used,
      limit,
      feature: GatedFeature.NativeChatMessageSend,
      context: PaywallContext.NativeChatMessageSend,
    });
  }

  /** Provisions a missing assistant before deriving its deterministic session id. */
  private async deriveSessionId(): Promise<string | null> {
    const spaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId;
    let assistantId = await getCurrentAssistantId({ store: this.store });
    if (spaceAccountId && !assistantId) {
      this.assistantIdProvisionPromise ??= (async () => {
        try {
          await this.store.memAgent.load();
          const loadedAssistantId = await getCurrentAssistantId({
            store: this.store,
          });
          if (loadedAssistantId) return loadedAssistantId;

          await this.store.memAgent.setup({ showToast: false });
          return getCurrentAssistantId({ store: this.store });
        } finally {
          this.assistantIdProvisionPromise = null;
        }
      })();
      assistantId = await this.assistantIdProvisionPromise;
    }

    if (!spaceAccountId || !assistantId) {
      logger.warn({
        message: "[FloatingAgentPageStore] Cannot derive the session id",
        info: {
          hasSpaceAccountId: !!spaceAccountId,
          hasAssistantId: !!assistantId,
        },
      });
      return null;
    }
    return resolveDefaultAgentChatSessionId({ spaceAccountId, assistantId });
  }

  private async findExistingSessionId(): Promise<string | null> {
    const sessionId = await this.deriveSessionId();
    if (!sessionId) return null;

    const row = await this.store.sessions.localTable.get(sessionId);
    return row ? sessionId : null;
  }

  private initializeEventsLiveQuery(): void {
    this.disposeWindowReaction?.();
    this.disposeWindowReaction = reaction(
      () => ({ sessionId: this.sessionId, limit: this.eventWindowLimit }),
      ({ sessionId, limit }) => {
        this.liveQuerySubscription?.unsubscribe();
        this.liveQuerySubscription = null;
        if (!sessionId) return;

        this.liveQuerySubscription = liveQuery(() =>
          this.store.sessionEvents.getManySessionEventsBySessionIdWindowed({
            sessionId,
            limit,
          })
        ).subscribe({
          next: rows => {
            runInAction(() => {
              this.subscribedSessionEvents = rows;
            });
          },
          error: error => {
            logger.error({
              message: "[FloatingAgentPageStore] session events live query failed",
              info: { error: String(error) },
            });
          },
        });
      },
      { fireImmediately: true }
    );
  }
}
